import apiClient, { getRoleFromToken, getSessionIdFromToken } from './api';
import { clearSessionCookie, getSessionCookie, getSiteMode, setSessionCookie } from '../utils/siteHosts';

const authService = {
  // Login - supports current API format: { user, tokens: { access, refresh } }
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login/', credentials);
    const data = response.data;
    const access = data?.tokens?.access ?? data?.token;
    const refresh = data?.tokens?.refresh;
    const user = data?.user;
    if (access) {
      localStorage.setItem('token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        const role = user.is_admin === true || user.role === 'admin' ? 'admin' : (user.role === 'manager' || user.is_manager === true ? 'manager' : 'user');
        setSessionCookie(role);
      } else {
        setSessionCookie('admin');
      }
    }
    return data;
  },

  // Logout
  logout: async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const refresh = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
      const sessionId = getSessionIdFromToken(token);
      await apiClient.post('/auth/logout/', {
        refresh,
        session_id: sessionId,
        token,
      });
    } catch (_) {
      // ignore network errors so client session reset still completes
    } finally {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      }
      clearSessionCookie();
    }
  },

  // Register
  register: async (userData) => {
    const response = await apiClient.post('/auth/register/', userData);
    return response.data;
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    if (!user) return null;
    try {
      const parsed = JSON.parse(user);
      const token = localStorage.getItem('token');
      const tokenRole = getRoleFromToken(token);
      if (tokenRole) {
        const expectedRole = tokenRole === 'admin' ? 'admin' : (tokenRole === 'manager' ? 'manager' : 'user');
        if (parsed.role !== expectedRole) {
          parsed.role = expectedRole;
          parsed.is_admin = expectedRole === 'admin';
          parsed.is_manager = expectedRole === 'manager';
          localStorage.setItem('user', JSON.stringify(parsed));
        }
      }
      return parsed;
    } catch {
      return null;
    }
  },

  // Alias for getCurrentUser
  getUser() {
    return this.getCurrentUser();
  },

  // Check if user is authenticated (checks localStorage token or cross-subdomain cookie)
  isAuthenticated: () => {
    return !!localStorage.getItem('token') || !!getSessionCookie();
  },

  // Where to send a user who already has a valid stored session (e.g. new tab on /login or landing).
  getAuthenticatedHomePath: (fromPath) => {
    const token = localStorage.getItem('token');
    const sessionCookie = getSessionCookie();
    const siteMode = getSiteMode();
    const canUseCookieOnlyRedirect = siteMode === 'marketing';
    if (!token && (!sessionCookie || !canUseCookieOnlyRedirect)) return null;

    try {
      let role = null;
      if (token) {
        const tokenRole = getRoleFromToken(token);
        if (tokenRole) {
          role = tokenRole === 'admin' ? 'admin' : (tokenRole === 'manager' ? 'manager' : 'user');
          try {
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
              const u = JSON.parse(rawUser);
              if (u && u.role !== role) {
                u.role = role;
                u.is_admin = role === 'admin';
                u.is_manager = role === 'manager';
                localStorage.setItem('user', JSON.stringify(u));
              }
            }
          } catch (_) {}
        } else {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          role = user.role;
        }
      }
      if (!role && sessionCookie) {
        role = sessionCookie;
      }
      const isManagement = role === 'admin' || role === 'manager';
      if (isManagement) {
        return (fromPath && fromPath !== '/login') ? fromPath : '/overview';
      }
      return (fromPath && fromPath.startsWith('/user/')) ? fromPath : '/user/my-work';
    } catch {
      return sessionCookie === 'user' ? '/user/my-work' : '/overview';
    }
  },


  // Forgot password: request reset link (sends email via same pipeline as invites)
  requestForgotPassword: async (email, frontendUrl) => {
    const response = await apiClient.post('/auth/forgot-password/', {
      email: (email || '').trim().toLowerCase(),
      ...(frontendUrl && { frontend_url: frontendUrl }),
    });
    return response.data;
  },

  // Validate reset token (uid + token from email link). No auth.
  validateResetToken: async (uid, token) => {
    const response = await apiClient.get('/auth/validate-reset-token/', {
      params: { uid, token },
    });
    return response.data;
  },

  // Reset password with uid + token from email link. No auth.
  resetPassword: async (uid, token, newPassword, confirmPassword) => {
    const response = await apiClient.post('/auth/reset-password/', {
      uid,
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    const data = response.data;
    // Store fresh tokens so the current session can stay alive after reset
    const access = data?.tokens?.access;
    const refresh = data?.tokens?.refresh;
    const user = data?.user;
    if (access) {
      localStorage.setItem('token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        const role = user.is_admin === true || user.role === 'admin'
          ? 'admin'
          : (user.role === 'manager' || user.is_manager === true ? 'manager' : 'user');
        setSessionCookie(role);
      }
    }
    return data;
  },

  // Invalidate all sessions except the current one (called after password change/reset if user opts in)
  logoutOtherDevices: async () => {
    const response = await apiClient.post('/auth/logout-other-devices/');
    return response.data;
  },
};

export default authService;
